import type { Document } from '@contentful/rich-text-types';
import type { Article } from '@shaojiang/types/article';

/**
 * Presentation helpers for Contentful articles: a topic for the filters, a short word for the
 * generated cover, and a reading time. All derived, so authors don't have to fill in anything.
 */

const TOPICS: { topic: string; pattern: RegExp }[] = [
  { topic: 'Snippets', pattern: /snippet/i },
  { topic: 'Testing', pattern: /\b(jest|cypress|vitest|test|testing|playwright)\b/i },
  {
    topic: 'DevOps',
    pattern: /\b(nginx|http\/?3|quic|ssh|https|deploy|publish|server|docker|domain)/i,
  },
  { topic: 'Next.js', pattern: /next\.?js|bundle|contentful|markdown/i },
  { topic: 'React', pattern: /react|rsc|ssr|hook|redux|recoil/i },
];

export const articleTopic = (article: Pick<Article, 'title'>) =>
  TOPICS.find(({ pattern }) => pattern.test(article.title))?.topic ?? 'Front-end';

export const ARTICLE_TOPICS = [...TOPICS.map(({ topic }) => topic).reverse(), 'Front-end'];

/** Words that make a good cover, in the order they're preferred */
const GLYPHS = [
  'RSC',
  'SSR',
  'HTTP/3',
  'QUIC',
  'HTTPS',
  'Nginx',
  'SSH',
  'ESM',
  'Jest',
  'Cypress',
  'Markdown',
  'Bundle',
  'Snippets',
  'Contentful',
  'Docker',
  'Redux',
  'Recoil',
  'Hooks',
  'React',
  'Next.js',
];

export function articleGlyph(article: Pick<Article, 'title'>) {
  const title = article.title;
  const found = GLYPHS.filter((glyph) =>
    new RegExp(glyph.replace(/[./]/g, '\\$&'), 'i').test(title),
  );
  if (found.includes('SSR') && found.includes('RSC')) return 'SSR / RSC';
  if (found.length) return found[0];
  return title.split(/\s+/).slice(0, 2).join(' ');
}

function countWords(node: unknown): number {
  if (!node || typeof node !== 'object') return 0;
  const { value, content } = node as { value?: string; content?: unknown[] };
  const own = typeof value === 'string' ? value.split(/\s+/).filter(Boolean).length : 0;
  return (
    own + (Array.isArray(content) ? content.reduce<number>((n, c) => n + countWords(c), 0) : 0)
  );
}

/** Minutes to read at 220 words a minute (code snippets count a little) */
export function readingMinutes(article: Pick<Article, 'content'>) {
  const words = countWords(article.content.json as Document);
  const snippets = article.content.links.entries.block.length;
  return Math.max(1, Math.round(words / 220 + snippets * 0.5));
}

/** What lists and cards need from an article (small enough to send to the browser) */
export interface ArticleSummary {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  topic: string;
  glyph: string;
  minutes: number;
}

export const summarize = (article: Article): ArticleSummary => ({
  slug: article.slug,
  title: article.title,
  date: article.date,
  excerpt: article.excerpt,
  topic: articleTopic(article),
  glyph: articleGlyph(article),
  minutes: readingMinutes(article),
});
